import React from 'react';
import { Shape, Group } from 'react-konva';
import PetButton from './PetButton';
import { useStyle } from '../../contexts/StyleContext'; 
import { useNotification } from '../../contexts/NotificationContext'; 
import { useTranslation } from 'react-i18next'; // Importing translation hook

const Rhombus = ({
  shapeId, shapeType, x, y, sideWidth, color, buttons, rotation,
  draggable, onClick, selected, selectedButton, onButtonClick,
  onMouseEnter, onMouseLeave, onDragStart, onDragMove, onDragEnd,
  isDraggingShape, handleMouseEnterButton, handleMouseLeaveButton, hovered, hoveredItem, buttonLayout
}) => {


  const { petButtonRadius } = useStyle() || {};
  const { showNotification } = useNotification();
  const { t } = useTranslation(); // Hook to access translation

  const rhombusHeight = Math.sqrt(3) / 2 * sideWidth; // Height of the rhombus

  const buttonPositions = buttonLayout === "two-buttons"
  ? calculateButtonPositionsForTwoButtons(0, 0, sideWidth, rhombusHeight) : [{ x: sideWidth/2, y: 0}]; // Center position for single button

  function calculateButtonPositionsForTwoButtons(centerX, centerY, sideWidth, height) {

    if (typeof centerX !== 'number' || typeof centerY !== 'number' || typeof sideWidth !== 'number' || typeof height !== 'number') {

      const message = "Invalid position or size for rhombus panel";
      const extra = { centerX, centerY, sideWidth, height };  
      showNotification({
        notification: t('errors.snackbar.InvalidPositionorSizeRhombus'),
        type: 'error',
        extra, 
        isException: false,
      });
      
      return [
        { x: 0, y: 0 },
        { x: 0, y: 0 }
      ];
    }

    const positions = [
      { x: sideWidth/2, y: centerY - (height/3) }, // Top vertex
      { x: sideWidth/2, y: centerY + (height/3) }, // Bottom vertex
    ];

    return positions;
  }

  const handleSelect = (shapeId, e) => {
    e.cancelBubble = true;
    setTimeout(() => { // Ensures that the function runs after drag detection has completed.
      if (!isDraggingShape) {
          onClick(shapeId, e);
      }
    }, 0);
  };

  const handleButtonClick = (buttonId, shapeId, e) => {
    e.cancelBubble = true;
    onButtonClick(buttonId, shapeId, e);
  };

  return (
    <React.Fragment>
      <Group
        draggable={draggable}
        onDragStart={onDragStart}
        onDragMove={(e) => onDragMove(shapeId, e)} // Use the handleDragMove from Soundboard.js
        onDragEnd={(e) => onDragEnd(shapeId, e)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        rotation={rotation}
        x={x}
        y={y}
        offsetX={sideWidth / 2} // Center horizontally
        offsetY={0}             // Center vertically
      >
        <Shape
          sceneFunc={(context, shape) => {
            context.beginPath();
            const height = Math.sin(Math.PI / 3) * (hovered || selected ? sideWidth - 1 : sideWidth); // Adjust height when hovered or selected
            context.moveTo((hovered || selected ? 1 : 0), (hovered || selected ? 1 : 0));
            context.lineTo((hovered || selected ? sideWidth - 1 : sideWidth) / 2, (hovered || selected ? height - 1 : height));
            context.lineTo((hovered || selected ? sideWidth - 1 : sideWidth), (hovered || selected ? 1 : 0));
            context.lineTo((hovered || selected ? sideWidth - 1 : sideWidth) / 2, (hovered || selected ? -height + 1 : -height));
            context.closePath();
            context.fillStrokeShape(shape);
          }}
          shapeType={shapeType}
          sideWidth={sideWidth}
          fill={color}
          offsetX={0}
          offsetY={0}
          onClick={(e) => handleSelect(shapeId, e)}
          onPointerUp={(e) => handleSelect(shapeId, e)} // Works for both touch and mouse
          stroke={hovered && (hoveredItem.type === 'panel') ? '#dc03d1' : (selected ? '#e89b00' : '#dedede')}
          strokeWidth={hovered && (hoveredItem.type === 'panel') ? 3 : (selected ? 3 : 1)}
          /*stroke={hovered && (hoveredItem.type === 'panel') ? '#dc03d1' : (selected ? '#e89b00' : 'transparent')}
          strokeWidth={2}*/
          strokeScaleEnabled={false}
          lineJoin="round"
          shadowColor={selected ? 'orange' : 'none'}
          shadowBlur={selected ? 20 : 0} 
          shadowOffset={{ x: 0, y: 0 }} 
          shadowOpacity={selected ? 0.9 : 0}
        />

        {buttons.map((button, index) => (
          <PetButton
            key={button._id}
            id={button._id}
            buttonId={button._id}
            buttonLabel={button.buttonLabel}
            shapeId={shapeId}
            x={buttonPositions[index].x}
            y={buttonPositions[index].y}
            radius={parseFloat(petButtonRadius)}
            color={button.color}
            rotation={rotation}
            selected={selectedButton === button._id}
            onClick={(e) => handleButtonClick(button._id, shapeId, e)}
            onPointerUp={(e) => handleButtonClick(button._id, shapeId, e)} // Works for both touch and mouse
            onMouseEnter={(buttonId, e) => handleMouseEnterButton(buttonId, e)}
            onMouseLeave={handleMouseLeaveButton}
            hovered={hoveredItem.type === 'button' && hoveredItem.id === button._id}
          />
        ))}
      </Group>
    </React.Fragment>
  );
};

export default Rhombus;
